import { apiClient } from './apiClient';

class FinanceService {
  // Métodos para Transações Financeiras
  async getTransactions(params = {}) {
    const response = await apiClient.get('/finance/transactions', { params });
    return response.data;
  }
  async getTransactionsPage({ q = '', transaction_type = '', classification = '', product_id = '', asset_id = '', bank_id = '', subclassification_id = '', start_date = '', end_date = '', is_verified = undefined, offset = 0, limit = 25 } = {}) {
    const params = {
      q: q || undefined,
      transaction_type: transaction_type || undefined,
      classification: classification || undefined,
      product_id: product_id || undefined,
      asset_id: asset_id || undefined,
      bank_id: bank_id || undefined,
      subclassification_id: subclassification_id || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      is_verified: typeof is_verified === 'boolean' ? is_verified : undefined,
      skip: offset,
      limit
    };
    const response = await apiClient.get('/finance/transactions/page', { params });
    return response.data;
  }

  async getTransactionsTotals({ q = '', transaction_type = '', classification = '', product_id = '', asset_id = '', bank_id = '', subclassification_id = '', start_date = '', end_date = '', is_verified = undefined } = {}) {
    const params = {
      q: q || undefined,
      transaction_type: transaction_type || undefined,
      classification: classification || undefined,
      product_id: product_id || undefined,
      asset_id: asset_id || undefined,
      bank_id: bank_id || undefined,
      subclassification_id: subclassification_id || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      is_verified: typeof is_verified === 'boolean' ? is_verified : undefined,
    };
    const response = await apiClient.get('/finance/transactions/totals', { params });
    return response.data;
  }

  async getTransaction(transactionId) {
    const response = await apiClient.get(`/finance/transactions/${transactionId}`);
    return response.data;
  }

  async createTransaction(transactionData) {
    const response = await apiClient.post('/finance/transactions', transactionData);
    return response.data;
  }

  async updateTransaction(transactionId, transactionData) {
    const response = await apiClient.put(`/finance/transactions/${transactionId}`, transactionData);
    return response.data;
  }

  async verifyTransaction(transactionId) {
    const response = await apiClient.put(`/finance/transactions/${transactionId}/verify`);
    return response.data;
  }

  async deleteTransaction(transactionId) {
    await apiClient.delete(`/finance/transactions/${transactionId}`);
  }

  // --- Anexos de Transações ---
  async getTransactionAttachments(transactionId) {
    const response = await apiClient.get(`/finance/transactions/${transactionId}/attachments`);
    return response.data;
  }

  async presignTransactionAttachment(transactionId, { filename, content_type, size_bytes }) {
    const response = await apiClient.post(`/finance/transactions/${transactionId}/attachments/presign`, {
      filename,
      content_type,
      size_bytes
    });
    return response.data;
  }

  async completeTransactionAttachment(transactionId, attachmentId) {
    const response = await apiClient.post(`/finance/transactions/${transactionId}/attachments/${attachmentId}/complete`);
    return response.data;
  }

  async getTransactionAttachmentSignedUrl(transactionId, attachmentId, mode = 'view') {
    const response = await apiClient.get(`/finance/transactions/${transactionId}/attachments/${attachmentId}/signed-url`, {
      params: { mode }
    });
    return response.data;
  }

  async deleteTransactionAttachment(transactionId, attachmentId) {
    const response = await apiClient.delete(`/finance/transactions/${transactionId}/attachments/${attachmentId}`);
    return response.data;
  }

  // --- Anexos de Ativos ---
  async getAssetAttachments(assetId) {
    const response = await apiClient.get(`/finance/assets/${assetId}/attachments`);
    return response.data;
  }

  async presignAssetAttachment(assetId, { filename, content_type, size_bytes }) {
    const response = await apiClient.post(`/finance/assets/${assetId}/attachments/presign`, {
      filename,
      content_type,
      size_bytes
    });
    return response.data;
  }

  async completeAssetAttachment(assetId, attachmentId) {
    const response = await apiClient.post(`/finance/assets/${assetId}/attachments/${attachmentId}/complete`);
    return response.data;
  }

  async getAssetAttachmentSignedUrl(assetId, attachmentId, mode = 'view') {
    const response = await apiClient.get(`/finance/assets/${assetId}/attachments/${attachmentId}/signed-url`, {
      params: { mode }
    });
    return response.data;
  }

  async deleteAssetAttachment(assetId, attachmentId) {
    const response = await apiClient.delete(`/finance/assets/${assetId}/attachments/${attachmentId}`);
    return response.data;
  }

  // Upload direto para URL pré-assinada (S3 compat)
  async uploadToPresignedUrl(uploadUrl, headers = {}, file) {
    const effectiveHeaders = { ...headers };
    if (file?.type && !effectiveHeaders['Content-Type']) {
      effectiveHeaders['Content-Type'] = file.type;
    }

    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: effectiveHeaders,
      body: file
    });

    if (!response.ok) {
      const statusText = response.statusText || 'Upload falhou';
      throw new Error(`Falha ao enviar arquivo (${response.status}): ${statusText}`);
    }
  }

  // Métodos para Ativos
  async searchAssets({ q = '', status = '', offset = 0, limit = 50 } = {}) {
    try {
      const params = { q, status: status || undefined, offset, limit };
      const response = await apiClient.get('/finance/assets/simple', { params });
      const items = response.data?.data || [];
      return items.map(a => ({ id: a.id, label: a.name }));
    } catch (error) {
      console.error('Erro ao buscar ativos (finance):', error);
      return [];
    }
  }
  async getAssets(params = {}) {
    const response = await apiClient.get('/finance/assets', { params });
    return response.data;
  }
  async getAssetsPage({ q = '', status = '', category = '', product_id = '', managed_by = '', start_date = '', end_date = '', min_value = '', max_value = '', offset = 0, limit = 25 } = {}) {
    const params = {
      q: q || undefined,
      status: status || undefined,
      category: category || undefined,
      product_id: product_id || undefined,
      managed_by: managed_by || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
      min_value: min_value || undefined,
      max_value: max_value || undefined,
      skip: offset,
      limit
    };
    const response = await apiClient.get('/finance/assets/page', { params });
    return response.data;
  }

  async getAsset(assetId) {
    const response = await apiClient.get(`/finance/assets/${assetId}`);
    return response.data;
  }

  async createAsset(assetData) {
    const response = await apiClient.post('/finance/assets', assetData);
    return response.data;
  }

  async updateAsset(assetId, assetData) {
    const response = await apiClient.put(`/finance/assets/${assetId}`, assetData);
    return response.data;
  }

  // Solicitações de alteração de ativos (quando aquisição verificada)
  async createAssetEditRequest(assetId, payload) {
    const response = await apiClient.post(`/finance/assets/${assetId}/edit-requests`, payload);
    return response.data;
  }

  async getAssetEditRequestsPage({ offset = 0, limit = 25, status = '' } = {}) {
    const params = {
      skip: offset,
      limit,
      status_filter: status || undefined
    };
    const response = await apiClient.get('/finance/asset-edit-requests/page', { params });
    return response.data;
  }

  async getAssetEditRequest(requestId) {
    const response = await apiClient.get(`/finance/asset-edit-requests/${requestId}`);
    return response.data;
  }

  async approveAssetEditRequest(requestId, payload = {}) {
    const response = await apiClient.put(`/finance/asset-edit-requests/${requestId}/approve`, payload);
    return response.data;
  }

  async rejectAssetEditRequest(requestId, payload = {}) {
    const response = await apiClient.put(`/finance/asset-edit-requests/${requestId}/reject`, payload);
    return response.data;
  }

  async updateAssetStatus(assetId, statusData) {
    const response = await apiClient.put(`/finance/assets/${assetId}/status`, statusData);
    return response.data;
  }

  async advanceAssetStatus(assetId, actionData) {
    const response = await apiClient.post(`/finance/assets/${assetId}/advance-status`, actionData);
    return response.data;
  }

  async revertAssetStatus(assetId, actionData) {
    const response = await apiClient.post(`/finance/assets/${assetId}/revert-status`, actionData);
    return response.data;
  }

  async addPreparationCost(assetId, costData) {
    const response = await apiClient.post(`/finance/assets/${assetId}/add-preparation-cost`, costData);
    return response.data;
  }

  async getAssetInstallments(assetId) {
    const response = await apiClient.get(`/finance/assets/${assetId}/installments`);
    return response.data;
  }

  async saveAssetInstallments(assetId, payload) {
    const response = await apiClient.put(`/finance/assets/${assetId}/installments`, payload);
    return response.data;
  }

  async deleteAsset(assetId) {
    await apiClient.delete(`/finance/assets/${assetId}`);
  }

  async getAssetStatusHistory(assetId) {
    const response = await apiClient.get(`/finance/assets/${assetId}/status-history`);
    return response.data;
  }

  async getAssetTransactions(assetId) {
    const response = await apiClient.get(`/finance/assets/${assetId}/transactions`);
    return response.data;
  }

  async getAssetPreparationCosts(assetId) {
    const response = await apiClient.get(`/finance/assets/${assetId}/preparation-costs`);
    return response.data;
  }

  // Métodos para Custos de Preparação (endpoints dedicados)
  async getPreparationCosts(params = {}) {
    const response = await apiClient.get('/finance/preparation-costs', { params });
    return response.data;
  }

  async getPreparationCost(transactionId) {
    const response = await apiClient.get(`/finance/preparation-costs/${transactionId}`);
    return response.data;
  }

  async createPreparationCost(transactionData) {
    const response = await apiClient.post('/finance/preparation-costs', transactionData);
    return response.data;
  }

  async updatePreparationCost(transactionId, transactionData) {
    const response = await apiClient.put(`/finance/preparation-costs/${transactionId}`, transactionData);
    return response.data;
  }

  async deletePreparationCost(transactionId) {
    await apiClient.delete(`/finance/preparation-costs/${transactionId}`);
  }

  // Métodos para Dashboard e Relatórios
  async getFinancialSummary(params = {}) {
    const response = await apiClient.get('/finance/dashboard/summary', { params });
    return response.data;
  }

  async getFinancialSummaryByProduct(params = {}) {
    const response = await apiClient.get('/finance/dashboard/summary-by-product', { params });
    return response.data;
  }

  async getAssetSummary(params = {}) {
    const response = await apiClient.get('/finance/dashboard/assets', { params });
    return response.data;  
  }

  // Novos métodos otimizados
  async getAssetCostBreakdown(assetId) {
    const response = await apiClient.get(`/finance/assets/${assetId}/cost-breakdown`);
    return response.data;
  }

  async getAssetPerformanceMetrics() {
    const response = await apiClient.get('/finance/assets/performance-metrics');
    return response.data;
  }

  async getConsolidatedFinancialReport(params = {}) {
    const response = await apiClient.get('/finance/consolidated-report', { params });
    return response.data;
  }

  async recalculateAssetCosts() {
    const response = await apiClient.post('/finance/maintenance/recalculate-asset-costs');
    return response.data;
  }

  // Métodos para obter enums
  async getTransactionTypes() {
    const response = await apiClient.get('/finance/enums/transaction-types');
    return response.data;
  }

  async getTransactionClassifications() {
    const response = await apiClient.get('/finance/enums/transaction-classifications');
    return response.data;
  }

  async getAssetStatuses() {
    const response = await apiClient.get('/finance/enums/asset-statuses');
    return response.data;
  }

  // Métodos para relatórios PDF
  async generateFinancialReport(reportData) {
    const response = await apiClient.post('/finance/reports/financial-pdf', reportData, {
      responseType: 'blob'
    });
    
    // Criar blob e fazer download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `relatorio-financeiro-${new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' })}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async generateInventoryReport(reportData) {
    const response = await apiClient.post('/finance/reports/inventory-pdf', reportData, {
      responseType: 'blob'
    });
    
    // Criar blob e fazer download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `relatorio-inventario-${new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' })}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async exportTransactionsReportPdf(params = {}) {
    const response = await apiClient.get('/finance/transactions/report/pdf', {
      params,
      responseType: 'blob'
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `relatorio-transacoes-${new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' })}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  async exportTransactionsReportExcel(params = {}) {
    const response = await apiClient.get('/finance/transactions/report/excel', {
      params,
      responseType: 'blob'
    });

    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `relatorio-transacoes-${new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' })}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  // Métodos para Analytics de Performance de Sorteios
  async getRafflePerformanceRanking(params = {}) {
    const response = await apiClient.get('/finance/analytics/finance/raffle-performance', { params });
    return response.data;
  }

  // Método super leve para buscar produtos (evita analytics completo)
  async searchProducts({ q = '', offset = 0, limit = 50 } = {}) {
    try {
      const response = await apiClient.get('/finance/products/simple', { params: { q, offset, limit } });
      const products = response.data?.data || [];
      return products.map(product => ({ id: product.product_id, label: product.product_title }));
    } catch (error) {
      console.error('Erro ao buscar produtos (finance):', error);
      return [];
    }
  }

  // Listagem leve de produtos para páginas que ainda usam preload
  async getAvailableProducts() {
    try {
      // Evita o endpoint /finance/products (mais pesado). Usa o endpoint leve e cacheado.
      const response = await apiClient.get('/finance/products/simple', {
        params: { limit: 500, offset: 0 }
      });
      const products = response.data?.data || [];
      return products.map(p => ({ id: p.product_id, title: p.product_title }));
    } catch (error) {
      console.error('Erro ao carregar produtos (finance):', error);
      return [];
    }
  }

  // Métodos para Fornecedores (Suppliers)
  async getSuppliers(params = {}) {
    const response = await apiClient.get('/finance/suppliers', { params });
    return response.data;
  }

  async getSuppliersPage({ q = '', category = '', is_active = undefined, offset = 0, limit = 25 } = {}) {
    const params = {
      q: q || undefined,
      category: category || undefined,
      is_active: typeof is_active === 'boolean' ? is_active : undefined,
      skip: offset,
      limit
    };
    const response = await apiClient.get('/finance/suppliers/page', { params });
    return response.data;
  }

  async getSupplier(supplierId) {
    const response = await apiClient.get(`/finance/suppliers/${supplierId}`);
    return response.data;
  }

  async createSupplier(supplierData) {
    const response = await apiClient.post('/finance/suppliers', supplierData);
    return response.data;
  }

  async updateSupplier(supplierId, supplierData) {
    const response = await apiClient.put(`/finance/suppliers/${supplierId}`, supplierData);
    return response.data;
  }

  async deleteSupplier(supplierId) {
    await apiClient.delete(`/finance/suppliers/${supplierId}`);
  }

  async searchSuppliers({ q = '', offset = 0, limit = 50 } = {}) {
    try {
      const response = await apiClient.get('/finance/suppliers/simple', { params: { q, offset, limit } });
      const suppliers = response.data?.data || [];
      return suppliers.map(supplier => ({ id: supplier.id, label: supplier.name }));
    } catch (error) {
      console.error('Erro ao buscar fornecedores:', error);
      return [];
    }
  }

  // Métodos para Bancos (Banks)
  async getBanksPage({ q = '', is_active = undefined, offset = 0, limit = 25 } = {}) {
    const params = {
      q: q || undefined,
      is_active: typeof is_active === 'boolean' ? is_active : undefined,
      skip: offset,
      limit
    };
    const response = await apiClient.get('/finance/banks/page', { params });
    return response.data;
  }

  async getBank(bankId) {
    const response = await apiClient.get(`/finance/banks/${bankId}`);
    return response.data;
  }

  async createBank(bankData) {
    const response = await apiClient.post('/finance/banks', bankData);
    return response.data;
  }

  async updateBank(bankId, bankData) {
    const response = await apiClient.put(`/finance/banks/${bankId}`, bankData);
    return response.data;
  }

  async deleteBank(bankId) {
    await apiClient.delete(`/finance/banks/${bankId}`);
  }

  async searchBanks({ q = '', offset = 0, limit = 50 } = {}) {
    try {
      const response = await apiClient.get('/finance/banks/simple', { params: { q, offset, limit } });
      const banks = response.data?.data || [];
      return banks.map(bank => ({ id: bank.id, label: bank.code ? `${bank.code} - ${bank.name}` : bank.name }));
    } catch (error) {
      console.error('Erro ao buscar bancos:', error);
      return [];
    }
  }

  // Métodos para Subclassificações
  async getSubclassificationsPage({ q = '', classification = undefined, is_active = undefined, offset = 0, limit = 25 } = {}) {
    const params = {
      q: q || undefined,
      classification: classification || undefined,
      is_active: typeof is_active === 'boolean' ? is_active : undefined,
      skip: offset,
      limit
    };
    const response = await apiClient.get('/finance/subclassifications/page', { params });
    return response.data;
  }

  async getSubclassification(subclassificationId) {
    const response = await apiClient.get(`/finance/subclassifications/${subclassificationId}`);
    return response.data;
  }

  async createSubclassification(data) {
    const response = await apiClient.post('/finance/subclassifications', data);
    return response.data;
  }

  async updateSubclassification(subclassificationId, data) {
    const response = await apiClient.put(`/finance/subclassifications/${subclassificationId}`, data);
    return response.data;
  }

  async deleteSubclassification(subclassificationId) {
    await apiClient.delete(`/finance/subclassifications/${subclassificationId}`);
  }

  async searchSubclassifications({ q = '', classification = undefined, offset = 0, limit = 50 } = {}) {
    try {
      const response = await apiClient.get('/finance/subclassifications/simple', {
        params: {
          q,
          classification: classification || undefined,
          offset,
          limit
        }
      });
      const items = response.data?.data || [];
      return items.map(item => ({ id: item.id, label: item.name }));
    } catch (error) {
      console.error('Erro ao buscar subclassificações:', error);
      return [];
    }
  }

  async getDRE({ startDate, endDate, productId } = {}) {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (productId) params.product_id = productId;
    const response = await apiClient.get('/finance/dre', { params });
    return response.data;
  }

  async exportDREPdf({ startDate, endDate, productId, productName } = {}) {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (productId) params.product_id = productId;
    if (productName) params.product_name = productName;
    const response = await apiClient.get('/finance/dre/pdf', {
      params,
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    const parts = ['DRE'];
    if (productId) parts.push(productId.substring(0, 20));
    if (startDate) parts.push(startDate.replace(/-/g, ''));
    if (endDate) parts.push(endDate.replace(/-/g, ''));
    link.setAttribute('download', `${parts.join('_')}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

export const financeService = new FinanceService();